import { Touchable } from "@kalyzee/kast-app-web-components";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import PageContainer from "../../components/page/PageContainer";
import TableRecord from "../../components/tables/table-record";
import Pagination from "../../components/utils/Pagination";
import SearchInput from "../../components/utils/SearchInput";
import Select from "../../components/utils/Select";
import Colors from "../../constants/colors";
import { VHOST } from "../../constants/ovenmedia";
import { OvenMediaSocketResultEmittedCommand } from "../../helpers/socket";
import { SUBTITLES_FILTER_OPTIONS } from "../../helpers/subtitles";
import { toastError, toastSuccess } from "../../helpers/toast";
import { useSocketAppDispatch } from "../../hooks/app";
import { OvenMediaRecordSession } from "../../interfaces/context";
import { socketBuildRecord, socketDeleteRecord, socketGetRecordSessions, SocketGetSessionParams } from "../../store/socket/actions";
import styles from "./records.module.css";
import { useOvenMediaContexts } from "../../store/context/hooks";

export const LIMIT = 15;

export interface OvenMediaGetSessionsResponse {
  limit: number;
  skip: number;
  count: number;
  total: number;
  sessions: OvenMediaRecordSession[];
}

const RecordPage = () => {
  const { app } = useParams();
  const [loading, setLoading] = useState(true);

  const socketDispatch = useSocketAppDispatch();
  const sessionsRef = useRef<OvenMediaRecordSession[]>([]);
  const searchRef = useRef<string>();
  const seachTimeoutRef = useRef<NodeJS.Timeout>();
  // The « Sous-titres » filter: a comma-joined list of statuses the server is asked for ("" = all).
  const subtitlesFilterRef = useRef<string>("");
  const [subtitlesFilter, setSubtitlesFilter] = useState<string>("");
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(LIMIT);
  const [sort, setSort] = useState<{ field: string; direction: -1 | 1 }>({ field: "startedAt", direction: -1 });
  const [page, setPage] = useState<{ current: number; total: number }>({ current: 0, total: 1 });
  const contexts = useOvenMediaContexts();

  const loadSessions = async (p?: Partial<SocketGetSessionParams>) => {
    const params: SocketGetSessionParams = {
      vhost: p?.vhost ?? VHOST,
      app: p?.app ?? app,
      skip: p?.skip ?? skip,
      limit: p?.limit ?? limit,
      sortField: p?.sortField ?? sort.field,
      sort: p?.sort ?? sort.direction,
      status: p?.status,
      search: p?.search ?? searchRef.current,
      subtitlesStatus: p?.subtitlesStatus ?? (subtitlesFilterRef.current ? subtitlesFilterRef.current.split(",") : undefined),
    };
    const result = await socketDispatch<OvenMediaSocketResultEmittedCommand<any, OvenMediaGetSessionsResponse>>(socketGetRecordSessions(params));
    if (!result) return;
    if (result.error) {
      toastError("Une erreur est survenue lors de la récupération des données ..." /* TRANSLATION */);
    } else if (result.response) {
      sessionsRef.current = result.response?.sessions ?? [];
      const skip = result.response.skip;
      const limit = result.response.limit;
      const total = result.response.total;
      setPage({
        current: Math.ceil(skip / limit),
        total: Math.ceil(total / limit),
      });
      setSkip(skip);
      setLimit(limit);
    }
    setLoading(false);
  };

  const reloadSessions = async (p?: Partial<SocketGetSessionParams>) => {
    setLoading(true);
    loadSessions(p);
  };

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      loadSessions();
    }, 500);
  }, []);

  useEffect(() => {
    reloadSessions();
  }, [app, skip, sort, limit]);

  return (
    <PageContainer title={"Records" /* TRANSLATION */} subtitle={app} loading={loading}>
      <div style={{ width: "100%" }}>
        <div className={styles.searchRow}>
          <SearchInput
            key="searchinput"
            autoFocus
            defaultValue={searchRef.current}
            placeholder={"Search" /* TRANSLATE */}
            containerStyle={{ width: "200px", maxWidth: "90%" }}
            onChange={(event) => {
              const search = event.currentTarget.value;
              if (search === searchRef.current) return;
              searchRef.current = search;
              if (seachTimeoutRef.current) clearTimeout(seachTimeoutRef.current);
              seachTimeoutRef.current = setTimeout(() => {
                reloadSessions({ skip: 0 });
              }, 500);
            }}
          />
          <Touchable
            className={styles.refreshButton}
            onPress={async () => {
              reloadSessions();
            }}
          >
            {"Refresh" /* TRANSLATION */}
          </Touchable>
          <div className={styles.filterRow}>
            <span className={styles.filterLabel}>{"Sous-titres :" /* TRANSLATION */}</span>
            <Select
              containerClassName={styles.filterSelect}
              value={subtitlesFilter}
              placeholder={"tous" /* TRANSLATION */}
              options={SUBTITLES_FILTER_OPTIONS}
              onValue={(value) => {
                setSubtitlesFilter(value);
                subtitlesFilterRef.current = value;
                reloadSessions({ skip: 0 });
              }}
            />
          </div>
        </div>

        {sessionsRef.current?.length ? (
          <>
            <TableRecord
              showApp={app === undefined}
              sort={sort}
              onSort={(field, direction: -1 | 1) => {
                if (sort.field !== field || sort.direction !== direction) {
                  setSort({
                    field,
                    direction,
                  });
                }
              }}
              data={sessionsRef.current.map((record, index) => ({
                context: contexts.find((c) => c.server?._id === record._server),
                stream: record.liveSession!,
                session: record,
              }))}
              onBuildRecord={async (item, ffmpegCmd, immediate) => {
                const id = item.session.id;
                if (!id) return;
                const result = await socketDispatch(socketBuildRecord({ serverId: item.session._server, data: { recordSession: id, ffmpegCmd: ffmpegCmd?.length ? ffmpegCmd : undefined, immediate }}));
                reloadSessions()
                if (!result.error) {
                  toastSuccess("Le fichier est en cours de création...");
                } else {
                  toastError(`Impossible de générer le fichier vidéo ${id} : ${result.error}`);
                }
              }}
              onDeleteRecord={async (item) => {
                const id = item.session.id;
                if (!id) return;
                const result = await socketDispatch(socketDeleteRecord({ serverId: item.session._server, data: { recordSession: id }}));
                reloadSessions()
                if (!result.error) {
                  toastSuccess("L'enregistrement a bien été supprimé.");
                } else {
                  toastError(`Impossible de supprimer l'enregistrement ${id}`);
                }
              }}
              onRefresh={() => reloadSessions()}
            />
            <br />
            <Pagination
              textColor={Colors.getMainIndianKhaki()}
              textColorSelected={Colors.getMainWestCoast()}
              selected={page.current}
              count={page.total}
              onSelected={(selected: number) => {
                setSkip(selected * limit);
              }}
            />
          </>
        ) : (
          <div className={styles.noStream}>{"Aucun stream de trouvé ..." /* TRANSLATION */}</div>
        )}
      </div>
    </PageContainer>
  );
};

export default RecordPage;
